import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import type { HorizontalAlignment } from '@shared/types/WebsiteHeaderSettings.types';

type TextAlignmentToolbarProps = Readonly<{
  selectedAlignment: HorizontalAlignment;
  onAlignmentChange: (alignment: HorizontalAlignment) => void;
  scaleValue: number;
}>;

type AlignmentOptionItemProps = Readonly<{
  alignment: HorizontalAlignment;
  isSelected: boolean;
  label: string;
  onSelect: (e: React.MouseEvent, alignment: HorizontalAlignment) => void;
  applyScaling: (value: number) => number;
}>;

const AlignmentIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M28.9649 13H10.7111C10.5225 13 10.3416 13.0749 10.2083 13.2083C10.0749 13.3416 10 13.5225 10 13.7111C10 13.8997 10.0749 14.0806 10.2083 14.2139C10.3416 14.3473 10.5225 14.4222 10.7111 14.4222H28.9649C29.1535 14.4222 29.3344 14.3473 29.4677 14.2139C29.6011 14.0806 29.676 13.8997 29.676 13.7111C29.676 13.5225 29.6011 13.3416 29.4677 13.2083C29.3344 13.0749 29.1535 13 28.9649 13Z"
      fill="currentColor"
    />
    <path
      d="M23.9873 18.8441C24.1758 18.8441 24.3567 18.7691 24.4901 18.6358C24.6234 18.5024 24.6983 18.3216 24.6983 18.133C24.6983 17.9444 24.6234 17.7635 24.4901 17.6301C24.3567 17.4968 24.1758 17.4219 23.9873 17.4219H10.7111C10.5225 17.4219 10.3416 17.4968 10.2083 17.6301C10.0749 17.7635 10 17.9444 10 18.133C10 18.3216 10.0749 18.5024 10.2083 18.6358C10.3416 18.7691 10.5225 18.8441 10.7111 18.8441H23.9873Z"
      fill="currentColor"
    />
    <path
      d="M29.676 22.5548C29.676 22.3663 29.6011 22.1854 29.4677 22.052C29.3344 21.9187 29.1535 21.8438 28.9649 21.8438H10.7111C10.5225 21.8438 10.3416 21.9187 10.2083 22.052C10.0749 22.1854 10 22.3663 10 22.5548C10 22.7434 10.0749 22.9243 10.2083 23.0577C10.3416 23.191 10.5225 23.2659 10.7111 23.2659H28.9649C29.1535 23.2659 29.3344 23.191 29.4677 23.0577C29.6011 22.9243 29.676 22.7434 29.676 22.5548Z"
      fill="currentColor"
    />
    <path
      d="M10.7111 26.2656C10.5225 26.2656 10.3416 26.3405 10.2083 26.4739C10.0749 26.6073 10 26.7881 10 26.9767C10 27.1653 10.0749 27.3462 10.2083 27.4795C10.3416 27.6129 10.5225 27.6878 10.7111 27.6878H21.1429C21.3315 27.6878 21.5123 27.6129 21.6457 27.4795C21.779 27.3462 21.854 27.1653 21.854 26.9767C21.854 26.7881 21.779 26.6073 21.6457 26.4739C21.5123 26.3405 21.3315 26.2656 21.1429 26.2656H10.7111Z"
      fill="currentColor"
    />
  </svg>
);

const ALIGNMENT_OPTIONS: ReadonlyArray<HorizontalAlignment> = ['left', 'center', 'right'];

const AlignmentOptionItem: FC<AlignmentOptionItemProps> = ({ alignment, isSelected, label, onSelect, applyScaling }) => (
  <Flex
    paddingX={pxToRem(applyScaling(16))}
    paddingY={pxToRem(applyScaling(12))}
    cursor="pointer"
    _hover={{ backgroundColor: 'mono2' }}
    onClick={e => onSelect(e, alignment)}
    backgroundColor={isSelected ? 'mono2' : 'transparent'}
  >
    <TextV2
      typographyVariant="body2"
      fontFamily="Inter UI"
      color="mono14"
      fontWeight={isSelected ? 600 : 400}
      whiteSpace="nowrap"
      style={{ fontSize: pxToRem(applyScaling(15)) }}
    >
      {label}
    </TextV2>
  </Flex>
);

AlignmentOptionItem.displayName = 'AlignmentOptionItem';

export const TextAlignmentToolbar: FC<TextAlignmentToolbarProps> = props => {
  const { selectedAlignment, onAlignmentChange, scaleValue } = props;
  const [isOpen, setIsOpen] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const { tq } = useTranslation('joykit');

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const alignmentLabels: Record<HorizontalAlignment, string> = {
    left: tq`Left Aligned`,
    center: tq`Center Aligned`,
    right: tq`Right Aligned`,
  };

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  const handleSelect = useCallback(
    (e: React.MouseEvent, alignment: HorizontalAlignment) => {
      e.stopPropagation();
      onAlignmentChange(alignment);
      setIsOpen(false);
    },
    [onAlignmentChange],
  );

  return (
    <Flex
      ref={toolbarRef}
      position="relative"
      flexDirection="column"
      alignItems="center"
      zIndex="overlay"
      onClick={e => e.stopPropagation()}
    >
      <Flex
        padding={pxToRem(applyScaling(12))}
        borderRadius={pxToRem(applyScaling(32))}
        backgroundColor="white"
        boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
        color="mono14"
        pointerEvents="all"
      >
        <Flex
          width={pxToRem(applyScaling(40))}
          height={pxToRem(applyScaling(40))}
          alignItems="center"
          justifyContent="center"
          borderRadius={pxToRem(applyScaling(20))}
          _hover={{
            backgroundColor: 'mono2',
            color: '#4E06F2',
          }}
          _active={{
            backgroundColor: 'mono3',
            color: '#300495',
          }}
          cursor="pointer"
          onClick={handleToggle}
          __css={{ '& > svg': { width: pxToRem(applyScaling(40)), height: pxToRem(applyScaling(40)) } }}
        >
          <AlignmentIcon />
        </Flex>
      </Flex>

      {isOpen && (
        <Flex
          position="absolute"
          top={`calc(100% + ${pxToRem(applyScaling(8))})`}
          flexDirection="column"
          backgroundColor="white"
          borderRadius={pxToRem(applyScaling(12))}
          boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
          overflow="hidden"
          minWidth={pxToRem(applyScaling(180))}
          pointerEvents="auto"
        >
          {ALIGNMENT_OPTIONS.map(alignment => (
            <AlignmentOptionItem
              key={alignment}
              alignment={alignment}
              isSelected={selectedAlignment === alignment}
              label={alignmentLabels[alignment]}
              onSelect={handleSelect}
              applyScaling={applyScaling}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

TextAlignmentToolbar.displayName = 'TextAlignmentToolbar';
